/**
 * The top of Home: a slim hero with the main action, and — until the workbench
 * has a connection, a dataset and an evaluator — three setup cards that tick
 * themselves off as each exists. The cards go away once all three are done.
 */

import type { LucideIcon } from "lucide-react";
import { ArrowRight, Check, Database, Gauge, Plug, Plus } from "lucide-react";
import { Link } from "react-router";
import { ButtonLink } from "@/components/ui/button";
import { useHolds } from "@/components/use-holds";
import { useDatasets } from "@/features/datasets/hooks";
import { useEvaluators } from "@/features/evaluators/hooks";
import { useProject } from "@/features/projects/hooks";
import { ProjectAction } from "@/features/projects/project-action";
import { useConnections } from "@/features/settings/hooks";

type Target = { to: string; label: string };

interface Step {
  title: string;
  text: string;
  icon: LucideIcon;
  done: boolean;
  /** Where to go to do it — or, when your role can't, who to ask — and where to go once it is done. */
  todo: Target | string;
  doneLink: Target;
}

/** The three setup steps, or `null` while any of their lists is still loading. */
export function useSetupSteps(): Step[] | null {
  const admin = useHolds("connections:write");
  const { can } = useProject();
  const connections = useConnections();
  const datasets = useDatasets();
  const evaluators = useEvaluators();
  if (!connections.data || !datasets.data || !evaluators.data) return null;
  return [
    {
      title: "Connect a platform",
      text: "Add a connection to where your agents live, so the workbench can call them.",
      icon: Plug,
      done: connections.data.length > 0,
      todo: admin
        ? { to: "/settings/connections?add=1", label: "Add connection" }
        : "Ask a workbench admin to add a connection.",
      doneLink: { to: "/settings/connections", label: "View connections" },
    },
    {
      title: "Add a dataset",
      text: "Upload a CSV of inputs and expected outputs: the cases every run is tested on.",
      icon: Database,
      done: datasets.data.length > 0,
      todo: can("datasets:write")
        ? { to: "/datasets?new=1", label: "New dataset" }
        : "Ask a project member to add a dataset.",
      doneLink: { to: "/datasets", label: "View datasets" },
    },
    {
      title: "Add an evaluator",
      text: "Pick a code check or an LLM judge from the templates to score each answer.",
      icon: Gauge,
      done: evaluators.data.length > 0,
      todo: can("evaluators:write")
        ? { to: "/evaluators?new=1", label: "New evaluator" }
        : "Ask a project member to add an evaluator.",
      doneLink: { to: "/evaluators", label: "View evaluators" },
    },
  ];
}

function StepCard({ step, n }: { step: Step; n: number }) {
  const Icon = step.icon;
  const link = step.done ? step.doneLink : step.todo;
  return (
    <li
      className={`flex flex-col gap-3 rounded-[9px] border px-4 py-3.5 ${
        step.done ? "border-line-soft bg-well" : "border-line bg-surface"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="flex size-8 items-center justify-center rounded-lg bg-chip text-chip-ink">
          <Icon size={16} strokeWidth={1.9} aria-hidden />
        </span>
        {step.done ? (
          <span className="flex items-center gap-1 text-xs font-medium text-success">
            <span className="flex size-[18px] items-center justify-center rounded-full bg-success-bg">
              <Check size={11} strokeWidth={3} aria-hidden />
            </span>
            Done
          </span>
        ) : (
          <span className="font-mono text-xs text-faint">Step {n}</span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <h3 className={`text-sm font-semibold ${step.done ? "text-muted" : ""}`}>{step.title}</h3>
        <p className="text-[13px] leading-relaxed text-muted">{step.text}</p>
      </div>
      {typeof link === "string" ? (
        <span className="mt-auto text-[13px] text-muted">{link}</span>
      ) : (
        <Link
          to={link.to}
          className={`mt-auto inline-flex items-center gap-1 text-[13px] font-medium ${
            step.done ? "text-muted hover:text-ink" : "link"
          }`}
        >
          {link.label}
          <ArrowRight size={13} strokeWidth={2.2} aria-hidden />
        </Link>
      )}
    </li>
  );
}

export function HomeHero({ hasExperiments }: { hasExperiments: boolean }) {
  const { can } = useProject();
  const steps = useSetupSteps();
  const done = steps?.filter((s) => s.done).length ?? 0;
  const setUp = steps !== null && done === steps.length;
  const subtitle =
    setUp && !hasExperiments
      ? "You are set up. Run your first experiment to score an agent on every case."
      : "Run an agent over a dataset, score every answer, and compare versions side by side.";

  return (
    <section aria-label="Getting started" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-line bg-subtle px-5 py-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.01em]">Home</h1>
          <p className="text-[13px] text-muted">{subtitle}</p>
        </div>
        <ProjectAction permission="experiments:write">
          <ButtonLink variant="primary" to="/experiments/new">
            <Plus size={14} strokeWidth={2.2} aria-hidden />
            New experiment
          </ButtonLink>
        </ProjectAction>
      </div>
      {can("experiments:write") && steps && !setUp && (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between">
            <h2 className="text-[15px] font-semibold">Get started</h2>
            <span className="font-mono text-xs text-muted">
              {done} of {steps.length} done
            </span>
          </div>
          <ol className="grid gap-3 md:grid-cols-3">
            {steps.map((step, i) => (
              <StepCard key={step.title} step={step} n={i + 1} />
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
